export const platformContext = "LedgerBox Document Processing"

export const documentTypeKeys = [
  "bank_statement",
  "invoice",
  "receipt",
  "general_table",
  "pay_stub",
  "purchase_order",
  "brokerage_statement",
  "w2",
  "form_1099",
  "check_image",
  "merchant_statement",
] as const

export type DocumentTypeKey = (typeof documentTypeKeys)[number]

export const organizationRoles = ["admin", "member"] as const

export type OrganizationRole = (typeof organizationRoles)[number]

/** What an organization brings documents in for. Onboarding asks once. */
export const organizationCollectsFromValues = [
  "clients",
  "own_business",
] as const

export type OrganizationCollectsFrom =
  (typeof organizationCollectsFromValues)[number]

/** Derived state of an organization invitation; expired is computed from
 * expiresAt, the other three map to stamped timestamps. */
export const organizationInvitationStatuses = [
  "pending",
  "accepted",
  "revoked",
  "expired",
] as const

export type OrganizationInvitationStatus =
  (typeof organizationInvitationStatuses)[number]

export const apiKeyScopes = [
  "upload",
  "process",
  "status",
  "export",
  "download",
  "clients:read",
  "requests:write",
  "files:read",
  "files:write",
  "post:write",
] as const

export type ApiKeyScope = (typeof apiKeyScopes)[number]

/**
 * What each scope lets a key do, in the words the key dialog and the public
 * docs use. One table, so the settings screen, the API reference, and the
 * agent docs cannot describe a scope three different ways.
 *
 * The `process` key keeps its name on the wire and in the database; only its
 * label changed, because the work it gates is extraction review.
 */
export const apiKeyScopeDetails = {
  upload: {
    label: "Upload",
    description: "Upload documents for extraction",
  },
  process: {
    label: "Extract",
    description:
      "Extract a client's file, fix rows, clear flags, and approve documents",
  },
  status: {
    label: "Status",
    description: "Check documents, rows, and flags",
  },
  export: {
    label: "Export",
    description: "Make CSV, Excel, and QBO files from documents",
  },
  download: {
    label: "Download",
    description: "Download original files and exports",
  },
  "clients:read": {
    label: "Read clients",
    description: "See clients, their contacts, requests, and request lists",
  },
  "requests:write": {
    label: "Send requests",
    description: "Send requests and reminders to clients by email",
  },
  "files:read": {
    label: "Read files",
    description: "See the files clients sent",
  },
  "files:write": {
    label: "Add files",
    description: "Upload files to a client for extraction",
  },
  "post:write": {
    label: "Post to QuickBooks",
    description: "Post approved documents to a connected QuickBooks company",
  },
} as const satisfies Record<
  ApiKeyScope,
  { label: string; description: string }
>

/**
 * Scopes that carry an older scope with them. A key that may add files to a
 * client needs the upload routes to do it, so `files:write` satisfies a route
 * that asks for `upload`. The map only ever widens a new scope into the old
 * route it is built on; an old scope never gains a new power from it.
 */
export const apiKeyScopeImplies: Readonly<
  Partial<Record<ApiKeyScope, readonly ApiKeyScope[]>>
> = {
  "files:write": ["upload"],
}

/** Whether a key holding `granted` may call a route that requires `required`. */
export const apiKeyHasScope = (
  granted: readonly ApiKeyScope[],
  required: ApiKeyScope
): boolean =>
  granted.includes(required) ||
  granted.some((scope) => apiKeyScopeImplies[scope]?.includes(required))

export const processorCapabilities = [
  "classification",
  "splitting",
  "extraction",
  "normalization",
  "validation",
  "csv_export",
] as const

export type ProcessorCapability = (typeof processorCapabilities)[number]

export const processorRolloutStates = [
  "draft",
  "active",
  "canary",
  "deprecated",
  "disabled",
] as const

export type ProcessorRolloutState = (typeof processorRolloutStates)[number]

export type JsonPrimitive = string | number | boolean | null

export type JsonValue =
  | JsonPrimitive
  | { readonly [key: string]: JsonValue }
  | readonly JsonValue[]

export type JsonObject = { readonly [key: string]: JsonValue }
